import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

// True when a Radix "outside interaction" originated inside the STAFFED Agent
// (launcher or panel). Used to keep the agent interactive above a non-modal
// dialog without dismissing that dialog.
const _isStaffedAgentTarget = (e) => {
  const t = e?.detail?.originalEvent?.target || e?.target;
  return !!(t && typeof t.closest === "function" && t.closest("[data-staffed-agent]"));
};

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-300",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// ── Dialog scroll-collapse guard ─────────────────────────────────────
// DialogContent is ITSELF the scroll container (max-h + overflow-y-auto)
// and is sized `sm:h-fit`. A second, nested scroll container inside it
// cannot resolve its own height against a fit-content parent — WebKit
// gives it 0, Blink guesses — so the modal body vanishes in Safari and
// looks fine in Chrome.
// This guard previously tested for a CAUSE (flex-basis: 0). When that
// spelling was changed to `auto` the guard went blind and reported a
// clean result on a modal whose body measured 0px on screen. A guard
// that hunts one cause misses the next one. This tests for the DAMAGE:
// a scroll region rendering at zero height, and the nested-scroller
// structure that produces it — regardless of how it was spelled.
export function auditDialogFlex(root) {
  const scope = root || document;
  const hits = [];
  const dialogs = scope.querySelectorAll('[role="dialog"]');
  dialogs.forEach((dlg) => {
    if (dlg.getBoundingClientRect().height < 2) return; // not actually shown
    const scrollers = [];
    dlg.querySelectorAll('*').forEach((el) => {
      const cs = getComputedStyle(el);
      if (!/auto|scroll/.test(cs.overflowY)) return;
      scrollers.push(el);
      const h = el.getBoundingClientRect().height;
      // THE DAMAGE: a scroll region that renders at zero height. Any cause.
      if (h < 2 && (el.childElementCount > 0 || el.scrollHeight > 0)) {
        hits.push({
          type: 'collapsed',
          el: String(el.className || el.tagName),
          height: h,
          dialogHeight: Math.round(dlg.getBoundingClientRect().height),
        });
      }
    });
    // THE STRUCTURE that produces it: DialogContent already scrolls, so a
    // scroll container nested inside it is one scroller too many.
    const dlgScrolls = /auto|scroll/.test(getComputedStyle(dlg).overflowY);
    if (dlgScrolls && scrollers.length > 0) {
      hits.push({
        type: 'nested-scroller',
        count: scrollers.length,
        els: scrollers.map((e) => String(e.className || e.tagName)).slice(0, 5),
      });
    }
  });
  return hits;
}
if (typeof window !== 'undefined') window.auditDialogFlex = auditDialogFlex;

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    const t = setTimeout(() => {
      const hits = auditDialogFlex(document);
      if (hits.length) {
        console.error(
          '[dialog] scroll-collapse: a scroll region inside this dialog ' +
          'rendered at zero height, or the dialog has a nested scroller. ' +
          'DialogContent already scrolls — remove the inner overflow-y-auto.',
          hits
        );
      }
    }, 60);
    return () => clearTimeout(t);
  }, []);

  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      onOpenAutoFocus={(e) => e.preventDefault()}
      // Keep the STAFFED Agent usable over an open (non-modal) dialog: a click
      // or focus landing inside the agent must NOT dismiss the dialog. Any other
      // outside interaction still closes it as usual. Harmless for modal dialogs.
      onPointerDownOutside={(e) => { if (_isStaffedAgentTarget(e)) e.preventDefault(); }}
      onFocusOutside={(e) => { if (_isStaffedAgentTarget(e)) e.preventDefault(); }}
      onInteractOutside={(e) => { if (_isStaffedAgentTarget(e)) e.preventDefault(); }}
      // CONSTRAINT: this panel IS the scroll container (max-h + overflow-y-auto,
      // both breakpoints). Do NOT add another scroll container inside it — a
      // nested scroller cannot resolve its height against `sm:h-fit` and
      // collapses to zero in Safari while rendering fine in Chrome.
      className={cn(
        // Base styles.
        // focus:outline-none + focus-visible:outline-none: Radix focuses this
        // container on open so screen readers land inside the dialog. It is
        // tabindex="-1", so a person never tabs to it — but the browser still
        // painted its default focus ring around the whole panel, which read as
        // a white frame drawn over the design. Suppressed HERE, on the
        // container only. Every button, input and link inside keeps its own
        // focus ring, so keyboard navigation is unchanged.
        "fixed z-[60] flex flex-col w-full gap-4 border border-white/10 bg-[#0A0A12]/95 backdrop-blur-xl shadow-2xl shadow-black/50 text-white focus:outline-none focus-visible:outline-none",
        // Mobile: bottom sheet with iOS scroll fix
        "inset-x-0 bottom-0 max-h-[85vh] rounded-t-[20px] p-4 pb-8 overflow-y-auto overscroll-contain",
        // Mobile drag handle
        "before:content-[''] before:block before:sm:hidden before:mx-auto before:w-10 before:h-1 before:rounded-full before:bg-white/20 before:mb-1 before:shrink-0",
        // Desktop: centered floating card
        "sm:inset-0 sm:m-auto sm:max-w-lg sm:h-fit sm:max-h-[90vh] sm:rounded-2xl sm:p-6 sm:pb-6 sm:overflow-y-auto",
        // Animations
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom duration-300",
        className
      )}
      style={{ WebkitOverflowScrolling: 'touch' }}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-3 top-3 z-10 rounded-full p-2 bg-white/10 text-gray-300 hover:text-white hover:bg-white/20 transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 disabled:pointer-events-none hover:scale-110">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
